import {Injectable} from '@angular/core';
import {OfferPremiumInstallmentCode} from '../../../consts/offer-premium-installment-code.enum';
import {PriceFrequency} from '../../../models/offer/offer-modification.model';
import {InsuranceCompanyCode} from '../../../../shared/models/insurance-company-code.enum';
import {
  UniqaAssistanceCoverage,
  OfferModificationCode,
  PevnoAssistanceCoverage
} from '../../../consts/offer-modification-code.enum';
import {OfferModificationDestination} from '../../../consts/offer-modification-destination.enum';
import {
  BeesafeAssistanceCoverage,
  BeesafeNnwCoverage
} from '../../../../shared/sections/web/additional-options-beesafe-section/beesafe-coverage-comparision-dialog/beesafe-coverage-comparison-dialog.component';
import {ApplicationStateService} from '../../../../shared/services/application-state.service';

@Injectable({
  providedIn: 'root'
})
export class OfferModificationsConfigService {

  private readonly TRANSLATIONS_CONTAINER = 'OFFER_MODIFICATIONS';
  private readonly BEESAFE_TRANSLATIONS_CONTAINER = 'BEESAFE_OFFER_MODIFICATIONS';
  private readonly COMMON_CONFIG: OfferModificationsCommonConfig = {
    installmentsPriceFrequenciesMapping: [
      {
        installmentCode: OfferPremiumInstallmentCode.ONE_INSTALMENT,
        priceFrequency: PriceFrequency.ANNUALLY
      },
      {
        installmentCode: OfferPremiumInstallmentCode.TWO_INSTALMENT,
        priceFrequency: PriceFrequency.SEMI_ANNUALLY
      },
      {
        installmentCode: OfferPremiumInstallmentCode.FOUR_INSTALMENT,
        priceFrequency: PriceFrequency.QUARTERLY
      },
      {
        installmentCode: OfferPremiumInstallmentCode.TWELVE_INSTALMENT,
        priceFrequency: PriceFrequency.MONTHLY
      }
    ]
  };

  private readonly INSURANCE_COMPANIES_MODIFICATIONS_CONFIG: InsuranceCompanyModificationsConfig[] = [
    {
      insuranceCompanyCode: InsuranceCompanyCode.UNIQA,
      modificationsConfig: [
        {
          code: OfferModificationCode.AC_VARIANT,
          destination: OfferModificationDestination.AC_VARIANTS,
          tooltip: `${this.TRANSLATIONS_CONTAINER}.${OfferModificationCode.AC_VARIANT}.description`,
        },
        {
          code: OfferModificationCode.MINICASCO_VARIANT,
          destination: OfferModificationDestination.AC_VARIANTS,
          tooltip: `${this.TRANSLATIONS_CONTAINER}.${OfferModificationCode.MINICASCO_VARIANT}.description`,
        },
        {
          code: OfferModificationCode.AC_TERRITORIAL_RANGE,
          destination: OfferModificationDestination.AC_VARIANTS
        },
        {
          code: OfferModificationCode.AC_EXCESS,
          destination: OfferModificationDestination.AC_VARIANTS,
          tooltip: `${this.TRANSLATIONS_CONTAINER}.${OfferModificationCode.AC_EXCESS}.description`,
        },
        {
          code: OfferModificationCode.AC_EXCESS_THIEF,
          destination: OfferModificationDestination.AC_VARIANTS,
          tooltip: `${this.TRANSLATIONS_CONTAINER}.${OfferModificationCode.AC_EXCESS_THIEF}.description`,
        },
        {
          code: OfferModificationCode.AC_GSU,
          destination: OfferModificationDestination.AC_VARIANTS,
          tooltip: `${this.TRANSLATIONS_CONTAINER}.${OfferModificationCode.AC_GSU}.description`,
        },
        {
          code: OfferModificationCode.NNW,
          destination: OfferModificationDestination.ADDITIONAL_OPTIONS,
          tooltip: `${this.TRANSLATIONS_CONTAINER}.${OfferModificationCode.NNW}.description`,
          isOptionRecommended: (optionValue: any) => optionValue > 0,
          withSelect: true
        },
        {
          code: OfferModificationCode.ASSISTANCE,
          destination: OfferModificationDestination.ADDITIONAL_OPTIONS,
          isOptionRecommended: (optionValue: any) => optionValue === UniqaAssistanceCoverage.STANDARD
        },
        {
          code: OfferModificationCode.GLASS,
          destination: OfferModificationDestination.ADDITIONAL_OPTIONS,
          tooltip: `${this.TRANSLATIONS_CONTAINER}.${OfferModificationCode.GLASS}.description`,
          withSelect: true
        },
        {
          code: OfferModificationCode.REPLACEMENT_CAR,
          destination: OfferModificationDestination.ADDITIONAL_OPTIONS,
          tooltip: `${this.TRANSLATIONS_CONTAINER}.${OfferModificationCode.REPLACEMENT_CAR}.description`,
        },
        {
          code: OfferModificationCode.TIRES,
          destination: OfferModificationDestination.ADDITIONAL_OPTIONS,
          tooltip: `${this.TRANSLATIONS_CONTAINER}.${OfferModificationCode.TIRES}.description`,
        },
        {
          code: OfferModificationCode.LUGGAGE,
          destination: OfferModificationDestination.ADDITIONAL_OPTIONS,
          tooltip: `${this.TRANSLATIONS_CONTAINER}.${OfferModificationCode.LUGGAGE}.description`,
        },
        {
          code: OfferModificationCode.MEDIC,
          destination: OfferModificationDestination.ADDITIONAL_OPTIONS,
          tooltip: `${this.TRANSLATIONS_CONTAINER}.${OfferModificationCode.MEDIC}.description`,
        },
        {
          code: OfferModificationCode.REPAIR_PARTS,
          destination: OfferModificationDestination.AC_VARIANTS
        },
        {
          code: OfferModificationCode.PARTS_DEPRECIATION,
          destination: OfferModificationDestination.AC_VARIANTS,
          tooltip: `${this.TRANSLATIONS_CONTAINER}.${OfferModificationCode.PARTS_DEPRECIATION}.description`,
        },
        {
          code: OfferModificationCode.PRICE_LOSS,
          destination: OfferModificationDestination.ADDITIONAL_OPTIONS,
          tooltip: `${this.TRANSLATIONS_CONTAINER}.${OfferModificationCode.PRICE_LOSS}.description`,
        },
        {
          code: OfferModificationCode.GREEN_CARD,
          destination: OfferModificationDestination.ADDITIONAL_OPTIONS,
          tooltip: `${this.TRANSLATIONS_CONTAINER}.${OfferModificationCode.GREEN_CARD}.description`,
        },
      ]
    },
    {
      insuranceCompanyCode: InsuranceCompanyCode.BEESAFE,
      modificationsConfig: [
        {
          code: OfferModificationCode.AC_VARIANT,
          destination: OfferModificationDestination.AC_VARIANTS,
          tooltip: `${this.BEESAFE_TRANSLATIONS_CONTAINER}.${OfferModificationCode.AC_VARIANT}.description`
        },
        {
          code: OfferModificationCode.AC,
          destination: OfferModificationDestination.AC_VARIANTS
        },
        {
          code: OfferModificationCode.ASSISTANCE,
          destination: OfferModificationDestination.ADDITIONAL_OPTIONS,
          tooltip: `${this.BEESAFE_TRANSLATIONS_CONTAINER}.${OfferModificationCode.ASSISTANCE}.description`,
          isOptionRecommended: (optionValue: any) => optionValue === BeesafeAssistanceCoverage.STANDARD
        },
        {
          code: OfferModificationCode.TIRES,
          destination: OfferModificationDestination.ADDITIONAL_OPTIONS,
          tooltip: `${this.BEESAFE_TRANSLATIONS_CONTAINER}.${OfferModificationCode.TIRES}.description`,
        },
        {
          code: OfferModificationCode.BATTERY,
          destination: OfferModificationDestination.ADDITIONAL_OPTIONS,
          tooltip: `${this.BEESAFE_TRANSLATIONS_CONTAINER}.${OfferModificationCode.BATTERY}.description`,
        },
        {
          code: OfferModificationCode.NNW,
          destination: OfferModificationDestination.ADDITIONAL_OPTIONS,
          tooltip: `${this.BEESAFE_TRANSLATIONS_CONTAINER}.${OfferModificationCode.NNW}.description`,
          isOptionRecommended: (optionValue: any) => optionValue === BeesafeNnwCoverage.COMFORT
        },
        {
          code: OfferModificationCode.GLASS,
          destination: OfferModificationDestination.ADDITIONAL_OPTIONS,
          tooltip: `${this.BEESAFE_TRANSLATIONS_CONTAINER}.${OfferModificationCode.GLASS}.description`,
        },
        {
          code: OfferModificationCode.DIRECT_LOSS_SETTLEMENT,
          destination: OfferModificationDestination.ADDITIONAL_OPTIONS,
          tooltip: `${this.BEESAFE_TRANSLATIONS_CONTAINER}.${OfferModificationCode.DIRECT_LOSS_SETTLEMENT}.description`,
        },
        {
          code: OfferModificationCode.LEGAL_PROTECTION,
          destination: OfferModificationDestination.ADDITIONAL_OPTIONS,
          tooltip: `${this.BEESAFE_TRANSLATIONS_CONTAINER}.${OfferModificationCode.LEGAL_PROTECTION}.description`,
        },
        {
          code: OfferModificationCode.LUGGAGE,
          destination: OfferModificationDestination.ADDITIONAL_OPTIONS,
          tooltip: `${this.BEESAFE_TRANSLATIONS_CONTAINER}.${OfferModificationCode.LUGGAGE}.description`,
        },
        {
          code: OfferModificationCode.SPORTS_EQUIPMENT,
          destination: OfferModificationDestination.ADDITIONAL_OPTIONS,
          tooltip: `${this.BEESAFE_TRANSLATIONS_CONTAINER}.${OfferModificationCode.SPORTS_EQUIPMENT}.description`,
        }
      ]
    },
    {
      insuranceCompanyCode: InsuranceCompanyCode.PEVNO,
      modificationsConfig: [
        {
          code: OfferModificationCode.AC_VARIANT,
          destination: OfferModificationDestination.AC_VARIANTS,
          tooltip: `${this.TRANSLATIONS_CONTAINER}.${OfferModificationCode.AC_VARIANT}.description`,
        },
        {
          code: OfferModificationCode.MINICASCO_VARIANT,
          destination: OfferModificationDestination.AC_VARIANTS,
          tooltip: `${this.TRANSLATIONS_CONTAINER}.${OfferModificationCode.MINICASCO_VARIANT}.pevnoDescription`,
        },
        {
          code: OfferModificationCode.AC_TERRITORIAL_RANGE,
          destination: OfferModificationDestination.AC_VARIANTS
        },
        {
          code: OfferModificationCode.AC_EXCESS,
          destination: OfferModificationDestination.AC_VARIANTS,
          tooltip: `${this.TRANSLATIONS_CONTAINER}.${OfferModificationCode.AC_EXCESS}.description`,
        },
        {
          code: OfferModificationCode.AC_EXCESS_THIEF,
          destination: OfferModificationDestination.AC_VARIANTS,
          tooltip: `${this.TRANSLATIONS_CONTAINER}.${OfferModificationCode.AC_EXCESS_THIEF}.description`,
        },
        {
          code: OfferModificationCode.AC_GSU,
          destination: OfferModificationDestination.AC_VARIANTS,
          tooltip: `${this.TRANSLATIONS_CONTAINER}.${OfferModificationCode.AC_GSU}.description`,
        },
        {
          code: OfferModificationCode.RENEWABLE_INSURANCE_AMOUNT,
          destination: OfferModificationDestination.AC_VARIANTS,
          tooltip: `${this.TRANSLATIONS_CONTAINER}.${OfferModificationCode.RENEWABLE_INSURANCE_AMOUNT}.description`,
        },
        {
          code: OfferModificationCode.NNW,
          destination: OfferModificationDestination.ADDITIONAL_OPTIONS,
          tooltip: `${this.TRANSLATIONS_CONTAINER}.${OfferModificationCode.NNW}.description`,
          isOptionRecommended: (optionValue: any) => optionValue > 0,
          withSelect: true
        },
        {
          code: OfferModificationCode.ASSISTANCE,
          destination: OfferModificationDestination.ADDITIONAL_OPTIONS,
          isOptionRecommended: (optionValue: any) => optionValue === PevnoAssistanceCoverage.ECONOMIC
        },
        {
          code: OfferModificationCode.GLASS,
          destination: OfferModificationDestination.ADDITIONAL_OPTIONS,
          tooltip: `${this.TRANSLATIONS_CONTAINER}.${OfferModificationCode.GLASS}.description`,
          withSelect: true
        },
        {
          code: OfferModificationCode.REPLACEMENT_CAR,
          destination: OfferModificationDestination.ADDITIONAL_OPTIONS,
          tooltip: `${this.TRANSLATIONS_CONTAINER}.${OfferModificationCode.REPLACEMENT_CAR}.pevnoDescription`,
        },
        {
          code: OfferModificationCode.TIRES,
          destination: OfferModificationDestination.ADDITIONAL_OPTIONS,
          tooltip: `${this.TRANSLATIONS_CONTAINER}.${OfferModificationCode.TIRES}.description`,
        },
        {
          code: OfferModificationCode.LUGGAGE,
          destination: OfferModificationDestination.ADDITIONAL_OPTIONS,
          tooltip: `${this.TRANSLATIONS_CONTAINER}.${OfferModificationCode.LUGGAGE}.pevnoDescription`,
        },
        {
          code: OfferModificationCode.MEDIC,
          destination: OfferModificationDestination.ADDITIONAL_OPTIONS,
          tooltip: `${this.TRANSLATIONS_CONTAINER}.${OfferModificationCode.MEDIC}.description`,
        },
        {
          code: OfferModificationCode.REPAIR_PARTS,
          destination: OfferModificationDestination.AC_VARIANTS
        },
        {
          code: OfferModificationCode.PARTS_DEPRECIATION,
          destination: OfferModificationDestination.AC_VARIANTS,
          tooltip: `${this.TRANSLATIONS_CONTAINER}.${OfferModificationCode.PARTS_DEPRECIATION}.description`,
        },
        {
          code: OfferModificationCode.PRICE_LOSS,
          destination: OfferModificationDestination.ADDITIONAL_OPTIONS,
          tooltip: `${this.TRANSLATIONS_CONTAINER}.${OfferModificationCode.PRICE_LOSS}.description`,
        },
        {
          code: OfferModificationCode.GREEN_CARD,
          destination: OfferModificationDestination.ADDITIONAL_OPTIONS,
          tooltip: `${this.TRANSLATIONS_CONTAINER}.${OfferModificationCode.GREEN_CARD}.description`,
        },
      ]
    }
  ];

  get insuranceCompanyCode(): InsuranceCompanyCode {
    return this.stateService.insuranceCompanyCode;
  }

  get isUniqa(): boolean {
    return this.insuranceCompanyCode === InsuranceCompanyCode.UNIQA;
  }

  get isUniqaOrPevno(): boolean {
    return [InsuranceCompanyCode.UNIQA, InsuranceCompanyCode.PEVNO].includes(this.insuranceCompanyCode);
  }

  get isBeesafe(): boolean {
    return this.insuranceCompanyCode === InsuranceCompanyCode.BEESAFE;
  }

  get insuranceCompanyConfig(): InsuranceCompanyModificationsConfig {
    return this.INSURANCE_COMPANIES_MODIFICATIONS_CONFIG.find(config => config.insuranceCompanyCode === this.insuranceCompanyCode);
  }

  constructor(private stateService: ApplicationStateService) { }

  getPriceFrequency(installmentCode: string): PriceFrequency {
    const mappingItem = this.COMMON_CONFIG.installmentsPriceFrequenciesMapping.find(item => item.installmentCode === installmentCode);
    return mappingItem ? mappingItem.priceFrequency : null;
  }

  getModificationsForDestination(destination: OfferModificationDestination): OfferModificationConfig[] {
    return this.insuranceCompanyConfig ? this.insuranceCompanyConfig.modificationsConfig.filter(config => config.destination === destination) : [];
  }

  getModificationConfig(code: OfferModificationCode): OfferModificationConfig {
    return this.insuranceCompanyConfig ? this.insuranceCompanyConfig.modificationsConfig.find(config => config.code === code) : null;
  }
}

export interface OfferModificationsCommonConfig {
  installmentsPriceFrequenciesMapping: InstallmentPriceFrequency[];
}

export interface InstallmentPriceFrequency {
  installmentCode: OfferPremiumInstallmentCode;
  priceFrequency: PriceFrequency;
}

export interface InsuranceCompanyModificationsConfig {
  insuranceCompanyCode: InsuranceCompanyCode;
  modificationsConfig: OfferModificationConfig[];
}

export interface OfferModificationConfig {
  code: OfferModificationCode | string;
  destination: OfferModificationDestination;
  tooltip?: string;
  isOptionRecommended?: (optionValue: any) => boolean;
  withSelect?: boolean;
}
